import {useState} from 'react';
import {Group, Collapse, ThemeIcon, Text, NavLink, Stack} from '@mantine/core';
import {routes} from "@/routes.tsx";
import {
    IconChevronDown,
    IconChevronUp, IconPencil,
    IconPlus, IconTrash
} from '@tabler/icons-react';
import {matchRoutes, useNavigate} from "react-router-dom";
import OmniFetchTS from "@/util/OmniFetchTS.tsx";
import {openLinkModal} from "@/components/SidebarLinks/LinkModal/modal_utils.tsx";
import TooltipActionIcon from "@/components/TooltipActionIcon.tsx";
import {ILink} from "@/types/models/ILink.ts";
import {TablerWebfontIcon} from "@/components/icon_picker/TablerIconPicker.tsx";
import {ILinkCategory} from "@/types/models/ILinkCategory.ts";
import {openEditLinkCategoryModal} from "@/components/SidebarLinks/EditLinkCategoryModal/modal_utils.tsx";
import {getLongestMatch} from "@/util/RouterUtils.ts";

interface LinkProps {
    link: ILink
    editMode: boolean
    reloadLinks: () => void
    onInternalNavigation: () => void
}

function Link({link, editMode, reloadLinks, onInternalNavigation}: LinkProps) {
    function moveLink(action: "up" | "down") {
        OmniFetchTS("/api/v1/links/"+link.id+"/move/"+action, {method: "POST"}).then((json) => {
            if (json.success) {
                reloadLinks()
            }
        })
    }

    const navigate = useNavigate();

    let linkContents = <Stack gap={"xs"}>
        <Text fz={"sm"}>{link.name}</Text>
        {(editMode && typeof link.category_id != "string") && <Group gap={"xs"}>
            <TooltipActionIcon size={"sm"} label={"Move Link Down"} onClick={() => {
                moveLink("down")
            }}><IconChevronDown/></TooltipActionIcon>
            <TooltipActionIcon size={"sm"} label={"Move Link Up"} onClick={() => {
                moveLink("up")
            }}><IconChevronUp/></TooltipActionIcon>
            <TooltipActionIcon size={"sm"} label={"Edit Link"} onClick={() => {
                openLinkModal(link, false, reloadLinks)
            }}><IconPencil/></TooltipActionIcon>
            <TooltipActionIcon size={"sm"} c={"red"} label={"Delete Link"} onClick={() => {
                openLinkModal(link, true, reloadLinks)
            }}><IconTrash/></TooltipActionIcon>
        </Group>}
    </Stack>
    if (link.href.startsWith("/")) {
        const locationMatches = getLongestMatch(matchRoutes(routes, location.pathname))
        const linkMatches = getLongestMatch(matchRoutes(routes, link.href))
        return <NavLink
            component={editMode ? "span" : "a"}
            onClick={editMode ? undefined : (e) => {
                e.preventDefault()
                navigate(link.href)
                onInternalNavigation()
            }}
            href={link.href}
            label={linkContents}
            active={(linkMatches && locationMatches) ? linkMatches.route.path === locationMatches.route.path : false}
        />
    } else {
        return <NavLink
            component={editMode ? "span" : "a"}
            href={link.href}
            label={linkContents}
            active={location.pathname === link.href}
            target={"_blank"}
        />
    }
}

interface LinksGroupProps {
    id: string | number
    initiallyOpened?: boolean
    category: ILinkCategory
    links?: ILink[]
    editMode: boolean
    reloadLinks: () => void
    onInternalNavigation: () => void
}

export function LinksGroup({ category, initiallyOpened, links, reloadLinks, editMode, onInternalNavigation }: LinksGroupProps) {
    const hasLinks = Array.isArray(links);
    const [opened, setOpened] = useState(initiallyOpened);


    const items = (hasLinks ? links : []).map((link) => {
        return <Link key={link.id} link={link} editMode={editMode} reloadLinks={reloadLinks} onInternalNavigation={onInternalNavigation} />
    })

    function moveCategory(action: "up" | "down") {
        if (category != null) {
            OmniFetchTS("/api/v1/links/categories/"+category.id+"/move/"+action, {method: "POST"}).then((json) => {
                if (json.success) {
                    reloadLinks()
                }
            })
        }
    }

    return (
        <>
            <NavLink
                href={"#"}
                label={<Stack gap={"xs"}>
                    {category.name}
                    {editMode && <Group>
                        {category.id > 0
                            ? <Group gap={"xs"}>
                                <TooltipActionIcon size={"sm"} label={"Move Category Down"} onClick={(e) => {
                                    e.stopPropagation()
                                    e.preventDefault()
                                    moveCategory("down")
                                }}>
                                    <IconChevronDown/>
                                </TooltipActionIcon>
                                <TooltipActionIcon size={"sm"} label={"Move Category Up"} onClick={(e) => {
                                    e.stopPropagation()
                                    e.preventDefault()
                                    moveCategory("up")
                                }}>
                                    <IconChevronUp/>
                                </TooltipActionIcon>
                                <TooltipActionIcon size={"sm"} label={"Edit Category"} onClick={(e) => {
                                    e.stopPropagation()
                                    e.preventDefault()
                                    openEditLinkCategoryModal(category, reloadLinks)
                                }}>
                                    <IconPencil/>
                                </TooltipActionIcon>
                            </Group>
                            : category.id == -1 && <TooltipActionIcon size={"sm"} label={"Make Movable Category"} c={"green"} onClick={(e) => {
                                e.stopPropagation()
                                e.preventDefault()
                                OmniFetchTS("/api/v1/links/categories", {method: "POST"}, {
                                    name: "Services on Omni",
                                    icon: "sparkles"
                                }).then((json) => {
                                    if (json.success) {
                                        reloadLinks()
                                    }
                                })
                            }}><IconPlus/></TooltipActionIcon>
                        }
                    </Group>}
                </Stack>}
                opened={opened}
                onChange={setOpened}
                leftSection={<ThemeIcon variant="light" size={30}><TablerWebfontIcon size={"1.5em"} icon={category.icon}/></ThemeIcon>}
            >
                {items}
            </NavLink>
            {hasLinks ? <Collapse expanded={opened || false}></Collapse> : null}
        </>
    );
}